import { useEffect, useState, type Dispatch, type SetStateAction } from "react";

/**
 * `useState` variant that syncs to localStorage. Useful for calendar view
 * toggles (hide-discrete rows, "today only", sort key, ...) that shouldn't
 * reset on refresh. Prefix keys with `whosout:` to avoid collisions if the
 * domain is ever shared.
 *
 * Not for user preferences that should sync cross-device (like
 * `show_today_summary`) — those live in the DB via /me/settings.
 */
export function usePersistedState<T>(
  key: string,
  initial: T,
): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw !== null ? (JSON.parse(raw) as T) : initial;
    } catch {
      return initial;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* quota exceeded, private mode, etc. — silently give up */
    }
  }, [key, value]);
  return [value, setValue];
}
